/**
 * Phase 22 — InstallPrompt
 *
 * Tiny "Install Circleo" pill rendered only inside the Profile screen.
 * Behavior:
 *   - Listens for the `beforeinstallprompt` event (Chromium / Android).
 *   - Renders nothing on iOS (which doesn't fire the event) and nothing
 *     when the app is already running standalone.
 *   - Dismissals persist in localStorage so we don't nag the user.
 *   - One-tap install: triggers the saved prompt and hides itself on
 *     either outcome.
 */
import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const DISMISS_KEY = 'circleo.installPrompt.dismissed';

export default function InstallPrompt() {
  const { t } = useLanguage();
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined'
      && window.localStorage?.getItem(DISMISS_KEY) === '1'
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Hide if launched as an installed PWA already.
    const standalone =
      window.matchMedia?.('(display-mode: standalone)')?.matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setDismissed(true);
      return undefined;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setDismissed(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      // Ignore — close anyway.
    }
    setDeferred(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch { /* localStorage may be disabled */ }
  };

  if (dismissed || !deferred) return null;

  return (
    <div
      className="mt-4 mx-4 p-4 rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 flex items-center gap-3 shadow-sm"
      data-testid="install-prompt"
      role="region"
      aria-label={t('installPromptAria')}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">{t('installPromptTitle')}</p>
        <p className="text-xs text-gray-600 mt-0.5">{t('installPromptBody')}</p>
      </div>
      <button
        type="button"
        onClick={handleInstall}
        className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90"
        data-testid="install-prompt-cta"
      >
        {t('install')}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('dismiss')}
        title={t('dismiss')}
        className="text-gray-400 hover:text-gray-700 px-2 py-1"
        data-testid="install-prompt-dismiss"
      >
        ×
      </button>
    </div>
  );
}
